import React, { Component } from 'react';
import moment from 'moment-jalaali';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { IUsers, RootState } from 'store/store';
import { NumberOrNull } from 'store/entity/types';
import { convertDigitToProperFormat } from 'utils/number';

import messages from './Messages';
import { Spinner } from 'components/Button/style';
import { display } from 'components/ErrorView/ErrorDialog';
import {
  Line,
  Symbol,
  UserBox,
  InfoBox,
  UserName,
  UserPhoto,
  SymbolIcon,
  MessageText,
  MessageDate,
  ShadowInput,
  UnreadPulse,
  InputWrapper,
  InfoWrapper,
  ChatsListContainer,
  CenterText,
  SearchText,
  BoxWrapper,
} from './styles';
import { appDispatch } from 'store/store';
import {
  IConversation,
  requireConversationByUserId,
  requireConversationByUserIdFn,
  requireConversationsByTopic,
  requireConversationsByTopicFn,
} from 'store/conversation/conversationSlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { ICompany } from 'store/companies/companiesTypes';
import { IFileMessageContent } from 'store/messages/messagesSlice';
import i18n from '../../../../i18n';
import { getLanguageBaseOnTZ } from '../../../../utils/LanguageLocalStorage';

interface IStateToProps {
  users: IUsers['map'];
  chats: Array<IConversation>;
  company: ICompany;
  systemId: number;
  thisUser: IThisUserState;
}

interface IDispatchToProps {
  getUsers: (usersList: number[]) => ReturnType<typeof requireSeveralUserFn>;
  getConversationByTopic: (
    userId: number,
    topic: string
  ) => ReturnType<typeof requireConversationsByTopicFn>;
  getConversationByUserID: (
    userId: number
  ) => ReturnType<typeof requireConversationByUserIdFn>;
}

interface IOwnProps {
  companyId: number;
  onSelectChat: (conversationId: number) => void;
  selectedChat: NumberOrNull;
}

interface IComponentState {
  search: string;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

class ChatsList extends Component<IComponentProps> {
  state: IComponentState = {
    search: '',
  };

  componentDidMount() {
    const { company, thisUser, getUsers, getConversationByTopic } = this.props;
    const userId = thisUser.userId;
    getConversationByTopic(Number(userId!), 'direct')
      .then(() => {
        const companyMembersId = Object.keys(company.members);
        const participantsIds: number[] = this.props.chats.reduce(
          (ids: any, chat) => {
            const participantIds = chat.participantIds;
            if (participantIds.length > 0) {
              participantIds.map((id: number) => ids.push(id));
            }
            return ids;
          },
          []
        );
        const usersId = Array.from(
          new Set([...participantsIds, ...companyMembersId])
        );
        getUsers(usersId.map((it) => Number(it))).catch((err) => {
          display(err);
        });
      })
      .catch((err) => {
        display(err);
      });
  }

  handleSelectSearchedUser = (userId: number) => () => {
    const { onSelectChat, getConversationByUserID } = this.props;
    getConversationByUserID(userId).then(({ conversationId }) => {
      this.setState({ search: '' });
      onSelectChat(conversationId);
    });
  };

  handleOpenChat = (conversationId: number) => () => {
    this.props.onSelectChat(conversationId);
  };

  handleSearchUser = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const name = e.target.name;
    this.setState({ [name]: value });
  };

  render() {
    const { chats, users, company, thisUser, selectedChat, systemId } =
      this.props;
    const userId = Number(thisUser.userId);
    const companyMembersId = Object.keys(company.members).filter(
      (id) => id !== userId.toString()
    );
    const companyUsers = Object.values(users).filter((user) =>
      companyMembersId.includes(user!.id.toString())
    );

    const lang = getLanguageBaseOnTZ();

    return (
      <ChatsListContainer hideOnSmall={!!selectedChat}>
        <React.Fragment>
          <div style={{ padding: '1rem' }}>
            <InputWrapper>
              <ShadowInput
                id="search"
                name="search"
                type="text"
                value={this.state.search}
                onChange={this.handleSearchUser}
                placeholder={i18n.t('search', { ns: 'dialects' }) as string}
                autoComplete="off"
                style={{
                  margin: '0',
                  border: '1px solid #9e9e9e',
                  boxSizing: 'border-box',
                }}
              />
              <Symbol>
                <SymbolIcon type="search" />
              </Symbol>
            </InputWrapper>
          </div>
          <BoxWrapper>
            {this.state.search.length > 0 && (
              <React.Fragment>
                <CenterText>
                  <SearchText>
                    {i18n.t('searchUsers', { ns: 'dialects' }) as string}
                  </SearchText>
                </CenterText>
                {companyUsers
                  .filter((user) => user!.name.indexOf(this.state.search) > -1)
                  .map((user) => (
                    <UserBox
                      key={user!.id}
                      active={false}
                      onClick={this.handleSelectSearchedUser(user!.id)}
                    >
                      <UserPhoto icon="user" />
                      <InfoBox>
                        <UserName>{user!.name}</UserName>
                      </InfoBox>
                    </UserBox>
                  ))}
                <CenterText>
                  <SearchText>
                    {i18n.t('searchDirects', { ns: 'dialects' }) as string}
                  </SearchText>
                </CenterText>
              </React.Fragment>
            )}

            {Object.values(chats)
              .sort((a, b) => b.lastMessageDate - a.lastMessageDate)
              .map((chat) => {
                const chatName = chat.participantIds
                  .filter((id: number) => id !== userId && id !== systemId)
                  .reduce((name: string, pid: number) => {
                    return `${name} ${users[pid]?.name || messages.loading}`;
                  }, '');
                const messagesProperty = chat.messages;
                if (chatName.indexOf(this.state.search) === -1) {
                  return null;
                }
                const hasMessage = messagesProperty.length > 0;
                let chatMessage;
                if (hasMessage) {
                  const messageContent = messagesProperty[
                    messagesProperty.length - 1
                  ].content as IFileMessageContent;
                  const fileName = messageContent.fileName;
                  chatMessage = fileName ? fileName : messageContent.text;
                }
                const chatId = chat.id;
                const isActive = selectedChat === chatId;
                return (
                  <span key={chatId}>
                    <UserBox
                      key={chatId}
                      active={isActive}
                      onClick={this.handleOpenChat(chatId)}
                    >
                      <UserPhoto
                        icon="user"
                        customStyle={{ marginInlineEnd: '0.2rem' }}
                      />
                      <InfoWrapper>
                        <InfoBox>
                          <UserName>{chatName}</UserName>
                          <MessageDate>
                            {lang === 'fa'
                              ? convertDigitToProperFormat(
                                  moment(chat.lastMessageDate).format(
                                    'jD jMMMM HH:mm'
                                  )
                                )
                              : convertDigitToProperFormat(
                                  moment(chat.lastMessageDate)
                                    .locale('en')
                                    .format('D MMMM HH:mm')
                                )}
                          </MessageDate>
                        </InfoBox>
                        <InfoBox>
                          <MessageText>
                            {hasMessage
                              ? chatMessage
                              : (i18n.t('noReceivedMessages', {
                                  ns: 'dialects',
                                }) as string)}
                          </MessageText>
                          {!!messagesProperty.length &&
                            messagesProperty[messagesProperty.length - 1]
                              .sendTime > chat.myLastMessageSeen && (
                              <UnreadPulse />
                            )}
                          {!!messagesProperty.length &&
                            messagesProperty[messagesProperty.length - 1]
                              .uploading && (
                              <Spinner
                                color="primary"
                                viewBox="0 0 128 128"
                                style={{ alignSelf: 'flex-end' }}
                              >
                                <path
                                  className="path"
                                  d="M75.4 126.6a11.4 11.4 0 0 1-2.1-22.6 40.9 40.9 0 0 0 30.5-30.6 11.4 11.4 0 1 1 22.3 4.9h0a63.8 63.8 0 0 1-47.8 48.1v0a11.4 11.4 0 0 1-2.9z"
                                />
                              </Spinner>
                            )}
                        </InfoBox>
                      </InfoWrapper>
                    </UserBox>
                    <Line color={isActive ? 'blue' : 'gray'} />
                  </span>
                );
              })}
          </BoxWrapper>
        </React.Fragment>
      </ChatsListContainer>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const thisUser = state.thisUser;
  const company = state.companies.data[props.companyId];
  const users = state.users.map;

  const allConversations = state.conversations.data;
  const chats = Object.values(allConversations).filter(
    (conversation: any) => conversation.topic === 'direct'
  );
  const systemId = state.conversations.systemId;

  return { thisUser, company, users, chats, systemId };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    getConversationByTopic: (userId: number, topic: string) =>
      appDispatch(requireConversationsByTopic({ userId, topic })),
    getUsers: (usersList: number[]) =>
      appDispatch(requireSeveralUser({ usersList })),
    getConversationByUserID: (userId: number) =>
      appDispatch(requireConversationByUserId({ userId })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatsList);
